// What a photo is called (P-0161): its note when a person wrote one, else a plain made-up name from
// its category and kind ("Before photo", "Before 2"). An imported file came over with its file's name
// for a caption ("IMG_4532.jpg", a stored path); that name means nothing to the office or a tech, so it
// counts as no note and shows only as the original file's name. One rule for the office and the iPad.

/** A storage path: folders and a file, no spaces ("Attaches/1a2b…_photo_001-x7k2m9q4.jpg"). */
const STORED_PATH = /^[\w.-]+(\/[\w.-]+)+$/;
/** The id the store puts before a file's own name: 16 or more hex digits (a GUID's hyphens too), then "_". */
const ID_BEFORE = /^[0-9a-f][0-9a-f-]{15,}_(?=.)/i;
/** The random tail after it: "-" and 8 to 16 lowercase letters and digits, at least one of each. */
const RANDOM_AFTER = /-(?=[a-z0-9]*\d)(?=[a-z0-9]*[a-z])[a-z0-9]{8,16}$/;

/** A picture's, a video's or a document's file type, as cameras, phones and scanners name them. */
const MEDIA_EXT =
  'jpe?g|jpe|jfif|png|heic|heif|gif|webp|avif|tiff?|bmp|dng|cr2|nef|arw|' +
  'mp4|mov|m4v|avi|3gp|3g2|mkv|webm|wmv|mpe?g|hevc|' +
  'pdf|docx?|xlsx?|odt|rtf|pptx?';
/** One word with no spaces, or a copy's "name (3)", then its type: "IMG_4532.jpg", "image (3).png". */
const FILE_NAME = new RegExp(`^[^\\s/\\\\]+( \\(\\d+\\))?\\.(${MEDIA_EXT})$`, 'i');

/**
 * What a caption says as words: a person's own as written, and a file that came over with its storage
 * path for a caption by the file's own name ("photo_001.jpg"): no folder, no id before it, no random
 * tail after it.
 */
export function captionText(caption: string | null): string | null {
  if (!caption || !STORED_PATH.test(caption.trim())) return caption;
  const file = caption.trim().slice(caption.trim().lastIndexOf('/') + 1);
  const dot = file.lastIndexOf('.');
  const ext = dot > 0 ? file.slice(dot) : '';
  const stem = (dot > 0 ? file.slice(0, dot) : file).replace(ID_BEFORE, '').replace(RANDOM_AFTER, '');
  return stem ? `${stem}${ext}` : file;
}

/** A caption that is only a file's name or its stored path, never a person's words. */
export function isFileNameCaption(caption: string): boolean {
  const t = caption.trim();
  return STORED_PATH.test(t) || FILE_NAME.test(t);
}

/** The note on a photo: a person's own words, or null when it has none or only its file's name. */
export function photoNote(caption: string | null | undefined): string | null {
  if (!caption?.trim() || isFileNameCaption(caption)) return null;
  return caption;
}

/** The original file's own name, when that is all the caption is ("IMG_4532.jpg"); else null. */
export function photoFileName(caption: string | null | undefined): string | null {
  if (!caption?.trim() || !isFileNameCaption(caption)) return null;
  return captionText(caption.trim());
}

/** A category as the start of a name: "Before photo", "Before 2". Other and unknown ones have none. */
const NAME_WORD: Record<string, string> = {
  before: 'Before',
  after: 'After',
  problem: 'Problem',
  equipment: 'Equipment',
  document: 'Document',
  signature: 'Signature',
};

/** What a file is, for its name: what the server says it shows, else its kind and type. */
export interface NamedFile {
  category: string;
  kind: string;
  contentType?: string;
  /** attachments.list's `display`, when it says one. */
  display?: string;
}

type Sort = 'photo' | 'video' | 'pdf' | 'file';

function sortOf(p: NamedFile): Sort {
  const type = p.contentType ?? '';
  switch (p.display) {
    case 'image':
    case 'preparing':
    case 'unshown':
    case 'raw':
      return 'photo';
    case 'video':
    case 'download':
      return 'video';
    case 'pdf':
      return 'pdf';
    case 'other':
      return 'file';
  }
  if (p.kind === 'video' || type.startsWith('video/')) return 'video';
  if (p.kind === 'photo' || type.startsWith('image/')) return 'photo';
  return type === 'application/pdf' ? 'pdf' : 'file';
}

/**
 * A file's made-up title, for the viewer's heading when it has no note: "Before photo", "Equipment
 * video", "Photo" or "Video" for an other or unknown category, "PDF document" or "Document" for a file.
 */
export function photoTitle(p: NamedFile): string {
  const s = sortOf(p);
  if (s === 'pdf') return 'PDF document';
  if (s === 'file') return 'Document';
  const word = NAME_WORD[p.category];
  const noun = s === 'video' ? 'video' : 'photo';
  return word ? `${word} ${noun}` : s === 'video' ? 'Video' : 'Photo';
}

/** The word a list numbers a file by: its category's, else "Photo", "Video" or "Document". */
function listWord(p: NamedFile): string {
  const s = sortOf(p);
  return NAME_WORD[p.category] ?? (s === 'video' ? 'Video' : s === 'photo' ? 'Photo' : 'Document');
}

/**
 * Each file's name in a list, by id: its note when it has one, else its word and its place among the
 * files with that word in the order they were taken ("Before 1", "Before 2", "Photo 1"). Every file
 * counts toward the numbers, a noted one too, so adding or taking off a note never renumbers the rest;
 * one with no time taken goes after those with one, in the order given.
 */
export function photoNames<
  T extends NamedFile & { id: string; caption: string | null; capturedAt?: string | null },
>(files: readonly T[]): Map<string, string> {
  const at = (p: T) => {
    const t = p.capturedAt ? Date.parse(p.capturedAt) : NaN;
    return Number.isNaN(t) ? Infinity : t;
  };
  const inOrder = files.map((p, i) => ({ p, i })).sort((a, b) => at(a.p) - at(b.p) || a.i - b.i);
  const counts = new Map<string, number>();
  const names = new Map<string, string>();
  for (const { p } of inOrder) {
    const word = listWord(p);
    const n = (counts.get(word) ?? 0) + 1;
    counts.set(word, n);
    names.set(p.id, photoNote(p.caption) ?? `${word} ${String(n)}`);
  }
  return names;
}
